import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
    Dialog, DialogTitle, DialogContent, DialogActions, Button,
    Box, Stack, Grid, Typography, TextField, Divider
} from '@mui/material';

// --- HELPER FOR FORM ROWS ---
const ModalFormRow = ({ label, children, bold = false }) => (
    <Grid container spacing={2} alignItems="center" sx={{ mb: 1.5 }}>
        <Grid item xs={12} md={5}>
            <Typography variant="subtitle2" sx={{ fontWeight: bold ? 'bold' : 'normal', display: 'block' }}>
                {label}
            </Typography>
        </Grid>
        <Grid item xs={12} md={7}>{children}</Grid>
    </Grid>
);

export default function AddBondOfSecurityDialog({ open, onClose, matterId, onSave, initialData }) {
    const [formData, setFormData] = useState(initialData || {});

    useEffect(() => {
        if (open) setFormData(initialData || {});
    }, [open, initialData]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSave = async () => {
        try {
            await axios.post(`https://demo.insollex.com/api/legal-matter/${matterId}/bonds`, formData);
            if (onSave) onSave();
            onClose();
        } catch (error) {
            console.error("Error saving bond:", error);
            alert("Failed to save bond.");
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 1 } }}>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center' }}>
                <Box sx={{ width: 16, height: 16, border: '1px solid #1976d2', display: 'flex', alignItems: 'center', justifyContent: 'center', mr: 1 }}>
                    <Typography sx={{ fontSize: '0.6rem', color: '#1976d2' }}>Δ</Typography>
                </Box>
                Adding a Bond of security (New)
            </DialogTitle>

            <DialogContent sx={{ p: 0, bgcolor: '#fdfdfd' }}>
                {/* Single Tab Header Appearance */}
                <Box sx={{ bgcolor: '#f0f0f0', borderBottom: '1px solid #ccc', px: 2, py: 0.5 }}>
                    <Typography variant="caption" sx={{ fontSize: '0.75rem', borderBottom: '2px solid #1976d2', pb: 0.5, display: 'inline-block' }}>
                        Bond of security
                    </Typography>
                </Box>

                <Box sx={{ p: 3 }}>
                    <Stack spacing={0.5}>
                        <ModalFormRow label="Surety's Full Names/Name of Institution" bold>
                            <TextField 
                                name="suretyName"
                                value={formData.suretyName || ''}
                                onChange={handleChange}
                                fullWidth 
                                size="small" 
                                sx={{ bgcolor: '#fffde7' }} // Matches image_059c38 yellow highlight
                            />
                        </ModalFormRow>

                        {/* --- Section 1: Natural Person --- */}
                        <Typography variant="caption" sx={{ fontWeight: 'bold', mt: 1 }}>
                            1. Complete only if Surety is a Natural Person:
                        </Typography>
                        <Box sx={{ pl: 2, mt: 1 }}>
                            <ModalFormRow label="Surety's Identity Number">
                                <TextField name="suretyId" value={formData.suretyId || ''} onChange={handleChange} size="small" sx={{ width: '150px' }} />
                            </ModalFormRow>
                            <ModalFormRow label="Surety's Street Address">
                                <TextField name="suretyAddress" value={formData.suretyAddress || ''} onChange={handleChange} fullWidth size="small" />
                            </ModalFormRow>
                        </Box>

                        <Divider sx={{ my: 2 }} />

                        {/* --- Section 2: Institution --- */}
                        <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                            2. Complete only if Surety is an Institution:
                        </Typography>
                        <Box sx={{ pl: 2, mt: 1 }}>
                            <ModalFormRow label="Names of Person signing for Institution">
                                <TextField name="signerName" value={formData.signerName || ''} onChange={handleChange} fullWidth size="small" />
                            </ModalFormRow>
                            <ModalFormRow label="Capacity of Person signing for Institution">
                                <TextField name="signerCapacity" value={formData.signerCapacity || ''} onChange={handleChange} fullWidth size="small" />
                            </ModalFormRow>
                        </Box>

                        <Divider sx={{ my: 2 }} />

                        {/* --- Shared Fields --- */}
                        <ModalFormRow label="Amount of Surety (in figures)">
                            <TextField name="amount" value={formData.amount || '0.00'} onChange={handleChange} size="small" sx={{ width: '150px', '& .MuiInputBase-input': { textAlign: 'right' } }} />
                        </ModalFormRow>
                        <ModalFormRow label="Surety's Domicilium Address">
                            <TextField name="domiciliumAddress" value={formData.domiciliumAddress || ''} onChange={handleChange} fullWidth size="small" />
                        </ModalFormRow>
                        <ModalFormRow label="Full Names of Joint Surety (if applicable)">
                            <TextField name="jointSuretyName" value={formData.jointSuretyName || ''} onChange={handleChange} fullWidth size="small" />
                        </ModalFormRow>
                        <ModalFormRow label="Domicilium Address of joint surety (if applic)">
                            <TextField name="jointSuretyAddress" value={formData.jointSuretyAddress || ''} onChange={handleChange} fullWidth size="small" />
                        </ModalFormRow>
                    </Stack>
                </Box>
            </DialogContent>

            <DialogActions sx={{ p: 2, bgcolor: '#f5f5f5', borderTop: '1px solid #e0e0e0', justifyContent: 'center' }}>
                <Stack direction="row" spacing={1}>
                    <Button onClick={handleSave} variant="contained" size="small" sx={{ bgcolor: '#e0e0e0', color: '#000', textTransform: 'none', minWidth: '80px', border: '1px solid #999', boxShadow: 'none' }}>OK</Button>
                    <Button onClick={onClose} variant="contained" size="small" sx={{ bgcolor: '#e0e0e0', color: '#000', textTransform: 'none', minWidth: '80px', border: '1px solid #999', boxShadow: 'none' }}>Cancel</Button>
                </Stack>
            </DialogActions>
        </Dialog>
    );
}